import { DEFAULT_REMEDIATION_STEPS, DEFAULT_VARIANCE_CATEGORIES } from './constants.js';

const KEY = {
  iterations: 'wip.billingRecon.iterations.v1',
  reviewsByIteration: 'wip.billingRecon.reviewsByIteration.v1',
  activeIteration: 'wip.billingRecon.activeIteration.v1',
  categories: 'wip.billingRecon.categories.v1',
  remediations: 'wip.billingRecon.remediations.v1',
  drawerWidth: 'wip.billingRecon.drawerWidthPx.v1',
  drawerMode: 'wip.billingRecon.drawerMode.v1',
};

const safeParse = (raw, fallback) => {
  try {
    const v = JSON.parse(raw);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

const read = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(key);
  return raw == null ? fallback : safeParse(raw, fallback);
};

const write = (key, value) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

// ---- Iterations -------------------------------------------------------------
// One iteration = one reconciliation round within a service month. A month can
// have many iterations as files are re-sent and reviewed.
//
// Iteration shape:
//   { id, monthId, number, createdAt, label, gmFileName?, cpqFileName? }

export const loadIterations = () => {
  const stored = read(KEY.iterations, []);
  return Array.isArray(stored) ? stored : [];
};

export const saveIterations = (list) => write(KEY.iterations, list);

export const upsertIteration = (iteration) => {
  const list = loadIterations();
  const idx = list.findIndex((it) => it.id === iteration.id);
  const next = idx >= 0 ? { ...list[idx], ...iteration } : iteration;
  if (idx >= 0) list[idx] = next;
  else list.push(next);
  saveIterations(list);
  return next;
};

export const deleteIteration = (id) => {
  const list = loadIterations().filter((it) => it.id !== id);
  saveIterations(list);
  // Also clear its reviews
  const all = read(KEY.reviewsByIteration, {});
  if (all && typeof all === 'object' && id in all) {
    delete all[id];
    write(KEY.reviewsByIteration, all);
  }
};

export const loadActiveIterationByMonth = () => {
  const map = read(KEY.activeIteration, {});
  return map && typeof map === 'object' ? map : {};
};

export const saveActiveIteration = (monthId, iterationId) => {
  const map = loadActiveIterationByMonth();
  map[monthId] = iterationId;
  write(KEY.activeIteration, map);
};

// ---- Review state (per iteration) -------------------------------------------
// Shape: { [iterationId]: { [bac]: { reviewed, category, remediation, notes[] } } }

export const loadReviews = (iterationId) => {
  if (!iterationId) return {};
  const all = read(KEY.reviewsByIteration, {});
  return (all && all[iterationId]) || {};
};

export const overwriteReviews = (iterationId, reviewsForIteration) => {
  if (!iterationId) return;
  const all = read(KEY.reviewsByIteration, {});
  all[iterationId] = reviewsForIteration;
  write(KEY.reviewsByIteration, all);
};

export const saveReview = (iterationId, bac, patch) => {
  if (!iterationId) return patch;
  const all = read(KEY.reviewsByIteration, {});
  const byBac = (all[iterationId] = all[iterationId] || {});
  const prev = byBac[bac] || { reviewed: false, category: null, remediation: null, notes: [] };
  const next = { ...prev, ...patch };
  byBac[bac] = next;
  write(KEY.reviewsByIteration, all);
  return next;
};

export const appendNote = (iterationId, bac, textOrNote, author) => {
  if (!iterationId) return null;
  const all = read(KEY.reviewsByIteration, {});
  const byBac = (all[iterationId] = all[iterationId] || {});
  const prev = byBac[bac] || { reviewed: false, category: null, remediation: null, notes: [] };
  const note = typeof textOrNote === 'string'
    ? { id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, ts: new Date().toISOString(), text: textOrNote, author: author || 'Unknown', status: 'final' }
    : { id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, status: 'final', ...textOrNote };
  const next = { ...prev, notes: [...prev.notes, note] };
  byBac[bac] = next;
  write(KEY.reviewsByIteration, all);
  return next;
};

/** Replace the entire notes array for a BAC — used by the draft-note
 *  autosave workflow where we need to update or remove a specific note. */
export const setNotes = (iterationId, bac, notes) => {
  if (!iterationId) return null;
  const all = read(KEY.reviewsByIteration, {});
  const byBac = (all[iterationId] = all[iterationId] || {});
  const prev = byBac[bac] || { reviewed: false, category: null, remediation: null, notes: [] };
  const next = { ...prev, notes: Array.isArray(notes) ? notes : [] };
  byBac[bac] = next;
  write(KEY.reviewsByIteration, all);
  return next;
};

export const clearIterationReviews = (iterationId) => {
  if (!iterationId) return;
  const all = read(KEY.reviewsByIteration, {});
  delete all[iterationId];
  write(KEY.reviewsByIteration, all);
};

// ---- Autocomplete suggestions ----------------------------------------------
const loadList = (key, defaults) => {
  const stored = read(key, null);
  if (!Array.isArray(stored)) return [...defaults];
  const set = new Set();
  const out = [];
  for (const v of [...stored, ...defaults]) {
    if (v && !set.has(v)) { set.add(v); out.push(v); }
  }
  return out;
};

export const loadCategories = () => loadList(KEY.categories, DEFAULT_VARIANCE_CATEGORIES);
export const loadRemediations = () => loadList(KEY.remediations, DEFAULT_REMEDIATION_STEPS);

const pushUnique = (key, value, defaults) => {
  if (!value || !value.trim()) return loadList(key, defaults);
  const existing = read(key, null) || [];
  if (existing.includes(value)) return loadList(key, defaults);
  const next = [value, ...existing];
  write(key, next);
  return loadList(key, defaults);
};

export const rememberCategory = (value) => pushUnique(KEY.categories, value, DEFAULT_VARIANCE_CATEGORIES);
export const rememberRemediation = (value) => pushUnique(KEY.remediations, value, DEFAULT_REMEDIATION_STEPS);

// ---- Drawer width (px) -----------------------------------------------------
export const loadDrawerWidth = () => {
  const v = read(KEY.drawerWidth, null);
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
};

export const saveDrawerWidth = (px) => {
  if (typeof px !== 'number' || !Number.isFinite(px) || px <= 0) return;
  write(KEY.drawerWidth, Math.round(px));
};

export const loadDrawerMode = () => {
  const value = read(KEY.drawerMode, 'wide');
  return ['compact', 'wide', 'fullscreen'].includes(value) ? value : 'wide';
};

export const saveDrawerMode = (mode) => {
  if (!['compact', 'wide', 'fullscreen'].includes(mode)) return;
  write(KEY.drawerMode, mode);
};
